import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  getMarketingMessages,
  getMarketingMessage,
  createMarketingMessage,
  updateMarketingMessage,
  deleteMarketingMessage,
  sendMarketingMessage as sendMessage
} from '../api';

// Fetch all marketing messages
export const fetchMarketingMessages = createAsyncThunk(
  'marketing/fetchMessages',
  async (type, { rejectWithValue }) => {
    try {
      const response = await getMarketingMessages(type);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        'Failed to fetch marketing messages'
      );
    }
  }
);

// Fetch single marketing message
export const fetchMarketingMessage = createAsyncThunk(
  'marketing/fetchMessage',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getMarketingMessage(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        'Failed to fetch marketing message'
      );
    }
  }
);

// Create marketing message
export const createMessage = createAsyncThunk(
  'marketing/createMessage',
  async (messageData, { rejectWithValue }) => {
    try {
      const response = await createMarketingMessage(messageData);
      toast.success('Poruka je uspešno kreirana');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        'Failed to create marketing message';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update marketing message
export const updateMessage = createAsyncThunk(
  'marketing/updateMessage',
  async ({ id, messageData }, { rejectWithValue }) => {
    try {
      const response = await updateMarketingMessage({ id, messageData });
      toast.success('Poruka je uspešno ažurirana');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        'Failed to update marketing message';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete marketing message
export const deleteMessage = createAsyncThunk(
  'marketing/deleteMessage',
  async (id, { rejectWithValue }) => {
    try {
      await deleteMarketingMessage(id);
      toast.success('Poruka je uspešno obrisana');
      return id;
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        'Failed to delete marketing message';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Send marketing message
export const sendMarketingMessage = createAsyncThunk(
  'marketing/sendMessage',
  async (id, { rejectWithValue }) => {
    try {
      const response = await sendMessage(id);
      toast.success(`Poruka je poslata! Poslato: ${response.data.sentCount}, Neuspešno: ${response.data.failedCount}`);
      return { id, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        'Failed to send marketing message';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const marketingSlice = createSlice({
  name: 'marketing',
  initialState: {
    messages: [],
    currentMessage: null,
    loading: false,
    error: null
  },
  reducers: {
    clearCurrentMessage: (state) => {
      state.currentMessage = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch messages
      .addCase(fetchMarketingMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarketingMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
        state.error = null;
      })
      .addCase(fetchMarketingMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single message
      .addCase(fetchMarketingMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarketingMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMessage = action.payload;
        state.error = null;
      })
      .addCase(fetchMarketingMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create message
      .addCase(createMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages.unshift(action.payload);
        state.error = null;
      })
      .addCase(createMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update message
      .addCase(updateMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.messages.findIndex(msg => msg._id === action.payload._id);
        if (index !== -1) {
          state.messages[index] = action.payload;
        }
        if (state.currentMessage && state.currentMessage._id === action.payload._id) {
          state.currentMessage = action.payload;
        }
        state.error = null;
      })
      .addCase(updateMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete message
      .addCase(deleteMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = state.messages.filter(msg => msg._id !== action.payload);
        if (state.currentMessage && state.currentMessage._id === action.payload) {
          state.currentMessage = null;
        }
        state.error = null;
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Send message
      .addCase(sendMarketingMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMarketingMessage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.messages.findIndex(msg => msg._id === action.payload.id);
        if (index !== -1) {
          state.messages[index] = {
            ...state.messages[index],
            status: 'sent',
            sentAt: new Date(),
            sentCount: action.payload.sentCount,
            failedCount: action.payload.failedCount
          };
        }
        if (state.currentMessage && state.currentMessage._id === action.payload.id) {
          state.currentMessage = {
            ...state.currentMessage,
            status: 'sent',
            sentAt: new Date(),
            sentCount: action.payload.sentCount,
            failedCount: action.payload.failedCount
          };
        }
        state.error = null;
      })
      .addCase(sendMarketingMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearCurrentMessage, clearError } = marketingSlice.actions;
export default marketingSlice.reducer;

